/**
 * Format a timestamp as relative time (e.g., "Just now", "30 min ago", "Yesterday")
 */
export function formatRelativeTime(timestamp: string | null | undefined): string {
  if (!timestamp) return '';

  const now = new Date();

  // Handle bare datetime format (YYYY-MM-DD HH:MM:SS) by converting to ISO
  let dateString = timestamp;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timestamp)) {
    // Bare datetime without timezone - treat as UTC
    dateString = timestamp.replace(' ', 'T') + 'Z';
  }

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('[formatRelativeTime] Invalid date:', timestamp);
    return '';
  }

  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  // Just now (< 1 minute)
  if (diffSec < 60) {
    return 'Just now';
  }

  // Minutes ago (< 1 hour)
  if (diffMin < 60) {
    return diffMin === 1 ? '1 min ago' : `${diffMin} min ago`;
  }

  // Hours ago (< 24 hours)
  if (diffHour < 24) {
    return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  }

  // This week (2-6 days ago)
  if (diffDay < 7) {
    return `${diffDay} days ago`;
  }

  // Last week
  if (diffDay < 14) {
    return 'Last week';
  }

  // Weeks ago (< 1 month)
  const diffWeek = Math.floor(diffDay / 7);
  if (diffDay < 30) {
    return diffWeek === 2 ? '2 weeks ago' : `${diffWeek} weeks ago`;
  }

  // Months ago (< 1 year)
  const diffMonth = Math.floor(diffDay / 30);
  if (diffDay < 365) {
    return diffMonth === 1 ? '1 month ago' : `${diffMonth} months ago`;
  }

  // Years ago
  const diffYear = Math.floor(diffDay / 365);
  return diffYear === 1 ? '1 year ago' : `${diffYear} years ago`;
}
